import styled from "@emotion/styled";
import React from "react";
import { Control, Controller } from "react-hook-form";
import { Checkbox } from "primereact/checkbox";
import { useI18n } from "@core/i18n/i18n";
import { RoutesPath } from "@ui/navigation/routes";
import { ErrorMessage } from "@ui/components/common/error-message";
import { e2eTestId } from "@e2e/test-ids";

interface ConsentContainerProps {
	control: unknown;
	isDirectAccess?: boolean;
	documentsLocale: string;
}

export const ConsentContainer = ({
	control,
	isDirectAccess = false,
	documentsLocale,
}: ConsentContainerProps): JSX.Element => {
	const { format } = useI18n();
	const termsOfUseUrl = `${RoutesPath.RegisterTermsOfUse}?locale=${encodeURIComponent(documentsLocale)}`;
	const privacyNoticeUrl = `${RoutesPath.RegisterPrivacyNotice}?locale=${encodeURIComponent(documentsLocale)}`;

	return (
		<>
			{isDirectAccess && (
				<Controller
					name="agreeToLicensedPtConsent"
					control={control as Control<{ [key: string]: boolean }, Checkbox>}
					rules={{
						required: format("global.form.field.requiredError"),
					}}
					render={({ field, fieldState }) => (
						<>
							<CheckboxContainer>
								<Checkbox inputId="agreeToLicensedPtConsent" {...field} checked={field.value} />
								<label htmlFor="agreeToLicensedPtConsent">{format("global.register.licensedPt.confirmation")}</label>
							</CheckboxContainer>
							<StyledErrorMessage>{fieldState.error?.message}</StyledErrorMessage>
						</>
					)}
				/>
			)}
			<Controller
				name="consent"
				control={control as Control<{ [key: string]: boolean }, Checkbox>}
				rules={{
					required: format("global.register.consent.tou.and.pn.required"),
				}}
				render={({ field, fieldState }) => (
					<>
						<ConsentCheckboxContainer>
							<Checkbox {...field} inputId="consent" checked={field.value} id={e2eTestId.registerPt.consentCheckbox} />
							<label htmlFor="consent">
								{format("global.register.consent.tou.and.pn", {
									link: {
										className: "p-size-s-bold text-primary",
										onClick: event => {
											event.preventDefault();
											window.open(termsOfUseUrl, "_blank");
										},
									},
									link2: {
										className: "p-size-s-bold text-primary",
										onClick: event => {
											event.preventDefault();
											window.open(privacyNoticeUrl, "_blank");
										},
									},
								})}
							</label>
						</ConsentCheckboxContainer>
						<StyledErrorMessage>{fieldState.error?.message}</StyledErrorMessage>
					</>
				)}
			/>
			{!isDirectAccess && (
				<>
					<Controller
						name="agreeToPhysiomeEmails"
						control={control as Control<{ [key: string]: boolean }, Checkbox>}
						render={({ field }) => (
							<CheckboxContainer>
								<Checkbox inputId="mailPhysio" {...field} checked={field.value} />
								<label htmlFor="mailPhysio">{format("global.register.physiomeEmails.v1_6")}</label>
							</CheckboxContainer>
						)}
					/>
					<Controller
						name="agreeToBiogenEmails"
						control={control as Control<{ [key: string]: boolean }, Checkbox>}
						render={({ field }) => (
							<CheckboxContainer>
								<Checkbox inputId="mailBiogen" {...field} checked={field.value} />
								<label htmlFor="mailBiogen">{format("global.register.biogenEmails.v1_6")}</label>
							</CheckboxContainer>
						)}
					/>
				</>
			)}
		</>
	);
};

const CheckboxContainer = styled.div`
	display: flex;
	place-items: center;
	gap: 16px;
	margin-top: 12px;
`;
const ConsentCheckboxContainer = styled(CheckboxContainer)`
	margin-top: 24px;
`;
const StyledErrorMessage = styled(ErrorMessage)`
	margin-top: 8px;
`;
